import { useAuthStore } from '../store/authStore';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const AUTH_BASE_URL =
  process.env.REACT_APP_AUTH_URL || 'https://opeva-usuarios-trp-dev.jphlions.com';

async function request(path, options = {}) {
  const token = useAuthStore.getState().session?.accessToken;
  const { headers = {}, ...requestOptions } = options;
  const isFormData = requestOptions.body instanceof FormData;

  const response = await fetch(`${BASE_URL}${path}`, {
    ...requestOptions,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'message' in data
        ? data.message
        : response.statusText || 'Request failed';
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return data;
}

// Performs a GET request.
async function get(path) {
  return request(path, { method: 'GET' });
}

// Performs a POST request.
async function post(path, body) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Performs a PUT request.
async function put(path, body) {
  return request(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// Performs a PATCH request.
async function patch(path, body) {
  return request(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Performs a DELETE request.
async function remove(path) {
  return request(path, { method: 'DELETE' });
}

// Downloads a backend file and returns it as a base64 dataURL.
// Returns null if filename is empty, the request fails, or conversion fails.
async function fetchArchivoComoDataUrl(filename) {
  if (!filename) return null;
  try {
    const token = useAuthStore.getState().session?.accessToken;
    const response = await fetch(
      `${BASE_URL}/api/informes/archivo?filename=${encodeURIComponent(filename)}`,
      {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result ?? null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Uploads a firma (File/Blob) to an informe via multipart/form-data.
async function uploadFirma(idInforme, file) {
  const formData = new FormData();
  formData.append('file', file);
  return request(`/api/informes/${idInforme}/firma`, {
    method: 'POST',
    body: formData,
  });
}

// Sube un PDF generado en el cliente y devuelve la URL http(s) descargable sin JWT.
async function uploadPdfInforme(idInforme, blob, fileName) {
  const formData = new FormData();
  formData.append('file', blob, fileName || 'informe.pdf');
  const resp = await request(`/api/informes/${idInforme}/pdf`, {
    method: 'POST',
    body: formData,
  });
  return resp?.data?.url;
}

// Lista evidencias guardadas para un item de evaluación (solo activas).
async function fetchEvidenciasInforme(idItemEvaluacion) {
  if (!idItemEvaluacion) return [];
  try {
    const resp = await get(
      `/api/informes/respuestas/evidencia?item=${encodeURIComponent(idItemEvaluacion)}`
    );
    return Array.isArray(resp?.data) ? resp.data : [];
  } catch {
    return [];
  }
}

// Descarga una evidencia del servidor y la devuelve como dataURL.
async function fetchEvidenciaComoDataUrl(referencia) {
  if (!referencia) return null;
  try {
    const token = useAuthStore.getState().session?.accessToken;
    const response = await fetch(
      // `${BASE_URL}/api/informes/respuestas/evidencia/getFile/${encodeURIComponent(referencia)}`,
      `${BASE_URL}/api/informes/respuestas/evidencia/getFile?ref=${encodeURIComponent(referencia)}`,

      {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result ?? null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Sube una evidencia (File) asociada a una pregunta de un informe (multipart).
async function uploadEvidencia(idItemEvaluacion, idPreguntaItem, idEvidencia, file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('id_item_evaluacion', idItemEvaluacion);
  formData.append('id_pregunta_item', idPreguntaItem);
  formData.append('id_evidencia', idEvidencia);
  return request('/api/informes/respuestas/evidencia', {
    method: 'POST',
    body: formData,
  });
}

const api = {
  get,
  post,
  put,
  patch,
  delete: remove,
  uploadFirma,
  uploadPdfInforme,
  uploadEvidencia,
  fetchArchivoComoDataUrl,
  fetchEvidenciasInforme,
  fetchEvidenciaComoDataUrl,
};

export default api;
export { AUTH_BASE_URL, BASE_URL };
